import {Directive, ElementRef, NgZone, OnDestroy, OnInit} from '@angular/core';

/**
 * Permite desplazar una tabla con scroll arrastrando con el ratón desde cualquier punto: en horizontal y,
 * si la tabla tiene la cabecera fija (directiva cabeceraFija), también en vertical.
 *
 * Las filas abren el registro al hacer clic, y al soltar tras arrastrar el navegador también manda un
 * clic. Por eso se distingue por la distancia: si el ratón se ha movido más de UMBRAL_PX es un
 * arrastre, y el clic de después se corta aquí (en fase de captura) antes de llegar a la fila; si no,
 * es un clic normal y pasa. En pantallas táctiles no hace falta: el dedo ya desplaza por sí solo.
 *
 * Los eventos van fuera de la zona de Angular: un mousemove no tiene por qué repintar la aplicación.
 */
@Directive({
    selector: '[arrastrarScroll]'
})
export class ArrastrarScrollDirective implements OnInit, OnDestroy {
    /** Lo que hay que mover el ratón para que deje de ser un clic */
    public static readonly UMBRAL_PX = 5;
    /** Desde donde se arrastra no se arranca: hay que poder seleccionar el texto de un campo */
    private static readonly SIN_ARRASTRE = 'input, textarea, select, [contenteditable]';

    private _inicio: { x: number, y: number, scrollLeft: number, scrollTop: number } | null = null;
    private _arrastrando = false;

    constructor(private _el: ElementRef<HTMLElement>, private _zone: NgZone) {
    }

    ngOnInit(): void {
        this._zone.runOutsideAngular(() => {
            this._el.nativeElement.addEventListener('mousedown', this._alPulsar);
            this._el.nativeElement.addEventListener('click', this._alHacerClic, true);
            this._el.nativeElement.addEventListener('dragstart', this._alEmpezarDragNativo);
        });
    }

    ngOnDestroy(): void {
        this._el.nativeElement.removeEventListener('mousedown', this._alPulsar);
        this._el.nativeElement.removeEventListener('click', this._alHacerClic, true);
        this._el.nativeElement.removeEventListener('dragstart', this._alEmpezarDragNativo);
        this._dejarDeEscuchar();
    }

    private _alPulsar = (ev: MouseEvent) => {
        const el = this._el.nativeElement;
        this._arrastrando = false;
        const sinScroll = el.scrollWidth <= el.clientWidth && el.scrollHeight <= el.clientHeight;
        if (ev.button !== 0 || sinScroll
            || (ev.target as Element).closest?.(ArrastrarScrollDirective.SIN_ARRASTRE)) {
            return;
        }
        this._inicio = {x: ev.clientX, y: ev.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop};
        // En window y no en el elemento: el arrastre sigue aunque el ratón salga de la tabla
        window.addEventListener('mousemove', this._alMover);
        window.addEventListener('mouseup', this._alSoltar);
    };

    private _alMover = (ev: MouseEvent) => {
        const dx = ev.clientX - this._inicio.x;
        const dy = ev.clientY - this._inicio.y;
        if (!this._arrastrando && Math.max(Math.abs(dx), Math.abs(dy)) < ArrastrarScrollDirective.UMBRAL_PX) {
            return;
        }
        if (!this._arrastrando) {
            this._arrastrando = true;
            this._el.nativeElement.classList.add('arrastrando');
            window.getSelection()?.removeAllRanges();
        }
        this._el.nativeElement.scrollLeft = this._inicio.scrollLeft - dx;
        this._el.nativeElement.scrollTop = this._inicio.scrollTop - dy;
    };

    private _alSoltar = () => {
        this._dejarDeEscuchar();
        this._el.nativeElement.classList.remove('arrastrando');
        // Si se suelta fuera de la tabla no llega ningún clic que consuma la marca: se limpia en
        // cuanto el navegador haya repartido el clic de este mismo soltar
        if (this._arrastrando) {
            setTimeout(() => this._arrastrando = false);
        }
    };

    private _alHacerClic = (ev: MouseEvent) => {
        if (this._arrastrando) {
            ev.preventDefault();
            ev.stopPropagation();
            this._arrastrando = false;
        }
    };

    /** Sin esto, empezar a arrastrar sobre un enlace o una imagen arrastra el enlace, no la tabla */
    private _alEmpezarDragNativo = (ev: DragEvent) => {
        if (this._inicio) {
            ev.preventDefault();
        }
    };

    private _dejarDeEscuchar() {
        this._inicio = null;
        window.removeEventListener('mousemove', this._alMover);
        window.removeEventListener('mouseup', this._alSoltar);
    }
}
